import { Model } from 'dva'
import { SubFlow } from './types'
import { OrganisationsService } from '../../Service'
import { IsObject } from '../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'

const SubFlows: Model = {
  namespace: 'subFlows',
  state: {
    list: [],
  },
  effects: {
    *list(
      { useCache = true, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let subFlows = yield select((state: any) => state.subFlows.list)
        let organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        let flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
        let flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
        if (!(subFlows.length > 0 && useCache)) {
          subFlows = yield call(OrganisationsService.organisationsFlowsVersionsSubFlowsList, {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            fields: ['name', 'id', 'slug'],
          })
          yield put({ type: 'modifyList', payload: subFlows })
        }
        onSuccess(subFlows)
      } catch (err: any) {
        FailureHandler('Error fetching subFlows', err, onFailure)
      }
      setLoading(false)
    },
    *retrieve(
      { slug, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let subFlow = yield select((state: any) =>
          state.subFlows.list.find((subFlow: SubFlow) => subFlow.slug === slug)
        )
        let organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        let flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
        let flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
        if (!subFlow) {
          subFlow = yield call(OrganisationsService.organisationsFlowsVersionsSubFlowsRetrieve, {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            slug,
          })
        }
        yield put({ type: 'modifyList', payload: subFlow })
        onSuccess(subFlow)
      } catch (err) {
        FailureHandler('Could not find the subFlow!', err, onFailure)
      }
      setLoading(false)
    },
    *create(
      { data = {}, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        let flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
        let flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
        const response = yield call(OrganisationsService.organisationsFlowsVersionsSubFlowsCreate, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          requestBody: data,
        })
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
        setLoading(false)
        return response
      } catch (err) {
        FailureHandler('Error adding a subFlow', err, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      { slug, data, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        let flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
        let flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsSubFlowsPartialUpdate,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            slug: slug,
            requestBody: data,
          }
        )
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error editing a subFlow', err, onFailure)
      }
      setLoading(false)
    },
    *destroy(
      { slug, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        let flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
        let flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
        yield call(OrganisationsService.organisationsFlowsVersionsSubFlowsDestroy, {
          organisationUuid,
          slug: slug,
          flowUuid,
          flowConfigVersion,
        })
        yield put({ type: 'modifyList', payload: slug })
        onSuccess()
      } catch (err) {
        FailureHandler('Error deleting a subFlow', err, onFailure)
      }
      setLoading(false)
    },
    *undelete(
      { slug, id, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let organisationUuid = yield select(
          (state: any) => state.flowLayout.flow?.organisation?.uuid
        )
        let flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
        let flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsSubFlowsUndeleteRetrieve,
          {
            organisationUuid,
            slug: slug,
            flowUuid,
            flowConfigVersion,
            id: id,
          }
        )
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error undeleting a subFlow', err, onFailure)
      }
      setLoading(false)
    },
  },
  reducers: {
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: SubFlow) => l.id !== action.payload.id)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = list
          .filter((l: SubFlow) => !action.payload.map((p: SubFlow) => p.id).includes(l.id))
          .concat(action.payload)
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: SubFlow) => l.id !== action.payload)
      } else if (typeof action.payload === 'string') {
        // if the payload is an integer, it's a slug sent in delete operations, so remove it from the list
        list = list.filter((l: SubFlow) => l.slug !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    setList(state, action) {
      const { list } = action.payload
      if (!Array.isArray(list)) {
        console.error('Received invalid subflows list')
      }
      return {
        ...state,
        list: list,
      }
    },
    resetSubFlows(state, action) {
      return {
        ...state,
        list: [],
      }
    },
  },
}

export default SubFlows
